import { BehaviorSubject, Observable } from "rxjs";
import { SessionDto, ValidationError } from "@se/platform";
import { distinctUntilKeyChanged, filter, map } from "rxjs/operators";
import { Component } from "./component";
import { Form } from "./form";
import { AddressComponent, CheckboxComponent, CheckboxDropdownComponent, CheckboxListComponent, DropdownComponent, TextareaComponent, TextboxComponent } from "./formFields";
import { FormFieldComponent } from "./formFieldComponent";
import { IAddressLookupService, IAddressLookupServiceId } from "../services";
import { inject } from "inversify";
import { IMessagingService, IMessagingServiceId } from "../services";
import { ILoggingService } from "../services/interfaces/loggingService";
import { ResponseDto } from "@se/platform/forms/dtos/out/responses/responseDto";
import { FormDto } from "@se/platform/forms/dtos/out/formDto";

abstract class FormComponent extends Component implements Form {

    protected formSubmitting$ : BehaviorSubject<boolean>;
    protected validationErrors$ : BehaviorSubject<ValidationError[]>;
    protected fieldValue$ : BehaviorSubject<ResponseDto>;
    protected formFields: FormFieldComponent[] = [];

    constructor(componentEl: HTMLElement, 
        @inject(IMessagingServiceId) protected messagingService: IMessagingService,
        @inject(IAddressLookupServiceId) protected addressLookupService: IAddressLookupService,
        protected appInsights: ILoggingService    ) {
        super(componentEl, messagingService);

        this.formSubmitting$ = new BehaviorSubject<boolean>(false);
        this.validationErrors$ = new BehaviorSubject<ValidationError[]>([]);
        this.fieldValue$ = new BehaviorSubject<ResponseDto>(null);
    }

    // Triggered when a form starts submitting.
    public onSubmitting$(): Observable<boolean> {
        return this.formSubmitting$.pipe(
            filter(isSubmitting => isSubmitting)
        );
    }

    // Triggered when any validation error occurs during a form submission.
    public onValidationErrors$(): Observable<ValidationError[]> {
        return this.validationErrors$.pipe(
            filter(errors => errors != null && errors.length > 0)
        );
    }
    
    // Triggered when a validation error (related to a specific form field) occurs during a form submission.
    public onValidationError$(fieldId: string): Observable<ValidationError> {
        return this.onValidationErrors$().pipe(
            map(errors => errors.find(error => error.fieldName === fieldId)),
            filter(error => error != null)
        );
    }

    // Triggered when any form field value changes.
    public onAnyValueChanged$(): Observable<ResponseDto> {
        return this.fieldValue$.pipe(
            filter(response => response != null)
        );
    }

    // Triggered when the value for a specific form field changes.
    public onValueChanged$(fieldId: string): Observable<ResponseDto> {
        return this.onAnyValueChanged$().pipe(
            filter(response => response.fieldId === fieldId),
            distinctUntilKeyChanged("value")
        );
    }

    protected LoadDynamicFormFieldComponents(session: SessionDto, formDto: FormDto) {

        // initialise each field in the form
        document.querySelectorAll<HTMLElement>("[class*=js-formField-component]").forEach(fieldEl => {

            // 1. find the corresponding fieldDto in our form
            const fieldId = fieldEl.dataset["fieldId"];
            const fieldDto = formDto.fields.find(f => f.id === fieldId);

            let fieldComponent: FormFieldComponent;

            if (fieldEl.classList.contains("js-textbox-component")) {
                fieldComponent = new TextboxComponent(fieldEl, this.messagingService, this);
            }
            else if (fieldEl.classList.contains("js-textarea-component")) {
                fieldComponent = new TextareaComponent(fieldEl, this.messagingService, this);
            }
            else if (fieldEl.classList.contains("js-dropdown-component")) {
                fieldComponent = new DropdownComponent(fieldEl, this.messagingService, this);
            }
            else if (fieldEl.classList.contains("js-checkbox-component")) {
                fieldComponent = new CheckboxComponent(fieldEl, this.messagingService, this);
            }
            else if (fieldEl.classList.contains("js-checkboxDropdown-component")) {
                fieldComponent = new CheckboxDropdownComponent(fieldEl, this.messagingService, this);
            }
            else if (fieldEl.classList.contains("js-checkboxList-component")) {
                fieldComponent = new CheckboxListComponent(fieldEl, this.messagingService, this);
            }
            else if (fieldEl.classList.contains("js-address-component")) {
                fieldComponent = new AddressComponent(fieldEl, this.messagingService, this, session, this.addressLookupService, this.appInsights);
            }

            this.formFields.push(fieldComponent);
        });

    }
}

export { FormComponent };